import { useState, useMemo, useEffect, useRef } from 'react';
import { apiClient } from '../config/api';
import useDebounce from './useDebounce';
import { debug } from '../utils/debugUtils';

/**
 * Custom hook for global search functionality across customers, orders, and quotes
 * @param {Object} options - Configuration options
 * @param {number} options.debounceDelay - Debounce delay for search input (default: 300ms)
 * @param {number} options.maxResults - Maximum results per category (default: 3)
 * @returns {Object} - Search state and handlers
 */
const useGlobalSearch = (options = {}) => {
  const {
    debounceDelay = 300,
    maxResults = 3
  } = options;

  // Search state
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [allData, setAllData] = useState({ customers: [], orders: [], quotes: [] });
  const [loading, setLoading] = useState(false);

  // Ref to store blur timeout ID
  const blurTimeout = useRef();

  // Debounced search query
  const debouncedQuery = useDebounce(query, debounceDelay);

  // Fetch all data for global search - back to direct approach
  useEffect(() => {
    debug.log('useGlobalSearch - Starting data fetch...');
    const fetchAllData = async () => {
      try {
        setLoading(true);
        debug.service.api('Fetching data using apiClient...');
        
        const [customersRes, ordersRes, quotesRes] = await Promise.all([
          apiClient.get('/customers'),
          apiClient.get('/orders'),
          apiClient.get('/quotes')
        ]);

        debug.service.api('API responses:', {
          customers: { status: customersRes.status },
          orders: { status: ordersRes.status },
          quotes: { status: quotesRes.status }
        });

        const customers = customersRes.data || [];
        const orders = ordersRes.data || [];
        const quotes = quotesRes.data || [];

        debug.service.api('Fetched data:', { 
          customers: customers.length, 
          orders: orders.length, 
          quotes: quotes.length 
        });
        
        setAllData({ customers, orders, quotes });
      } catch (error) {
        debug.error('Error fetching data for global search:', error);
        setAllData({ customers: [], orders: [], quotes: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Extract arrays safely
  const customers = Array.isArray(allData.customers) ? allData.customers : [];
  const orders = Array.isArray(allData.orders) ? allData.orders : [];
  const quotes = Array.isArray(allData.quotes) ? allData.quotes : [];

  // Search function for customers
  const searchCustomers = useMemo(() => {
    if (!debouncedQuery.trim() || !Array.isArray(customers)) return [];

    const searchTerm = debouncedQuery.toLowerCase();
    
    return customers.filter(customer => {
      if (!customer) return false;
      
      const searchableFields = [
        customer.email,
        customer.companyName,
        customer.company,
        customer.phone,
        customer.address,
        customer.name,
        customer.firstName,
        customer.lastName
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchTerm)
      );
    }).slice(0, maxResults);
  }, [customers, debouncedQuery, maxResults]);

  // Search function for orders
  const searchOrders = useMemo(() => {
    if (!debouncedQuery.trim() || !Array.isArray(orders)) return [];

    const searchTerm = debouncedQuery.toLowerCase();
    
    return orders.filter(order => {
      if (!order) return false;
      
      const searchableFields = [
        order.materials,
        order.companyName,
        order.company,
        order.status,
        order.email,
        order.userEmail,
        order.customer?.email,
        order.customer?.companyName,
        order.customer?.company,
        order.description,
        order.jobDescription
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchTerm)
      );
    }).slice(0, maxResults);
  }, [orders, debouncedQuery, maxResults]);

  // Search function for quotes
  const searchQuotes = useMemo(() => {
    if (!debouncedQuery.trim() || !Array.isArray(quotes)) return [];

    const searchTerm = debouncedQuery.toLowerCase();
    
    return quotes.filter(quote => {
      if (!quote) return false;
      
      const searchableFields = [
        quote.customer?.companyName,
        quote.customer?.company,
        quote.customer?.email,
        quote.userEmail,
        quote.status,
        quote.materials,
        quote.description,
        quote.jobDescription,
        quote.companyName,
        quote.company,
        quote.email
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchTerm)
      );
    }).slice(0, maxResults);
  }, [quotes, debouncedQuery, maxResults]);

  // Combined search results
  const searchResults = useMemo(() => {
    const customers = searchCustomers;
    const orders = searchOrders;
    const quotes = searchQuotes;
    const total = customers.length + orders.length + quotes.length;

    // Debug search results
    if (debouncedQuery.trim()) {
      debug.log('useGlobalSearch - Search results for:', debouncedQuery, {
        customers: customers.length,
        orders: orders.length,
        quotes: quotes.length,
        total
      });
    }

    return {
      customers,
      orders,
      quotes,
      total,
      hasResults: total > 0,
      isEmpty: total === 0 && debouncedQuery.trim().length > 0
    };
  }, [searchCustomers, searchOrders, searchQuotes, debouncedQuery]);

  // Search handlers
  const handleInputChange = (value) => {
    setQuery(value);
    setShowResults(value.length > 0);
  };

  const handleResultClick = () => {
    setQuery('');
    setShowResults(false);
  };

  const handleKeyDown = (key) => {
    if (key === 'Escape') {
      setQuery('');
      setShowResults(false);
    }
  };

  const handleFocus = () => {
    if (query.length > 0) {
      setShowResults(true);
    }
  };

  const handleBlur = () => {
    // Clear any existing timeout
    if (blurTimeout.current) {
      clearTimeout(blurTimeout.current);
    }
    // Delay hiding to allow clicks on results
    blurTimeout.current = setTimeout(() => setShowResults(false), 200);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeout.current) {
        clearTimeout(blurTimeout.current);
      }
    };
  }, []);

  // Clear search
  const clearSearch = () => {
    setQuery('');
    setShowResults(false);
  };

  // Helper functions for display
  const getCustomerDisplayName = (customer) => {
    return customer.companyName || customer.company || customer.name || customer.email || 'Unknown Customer';
  };

  const getCustomerEmail = (customer) => {
    return customer.email || 'No email';
  };

  const getOrderDisplayName = (order) => {
    return order.materials || order.description || order.jobDescription || 'Order';
  };

  const getOrderCompany = (order) => {
    return order.companyName || order.company || order.customer?.companyName || order.customer?.company || order.email || 'Unknown Company';
  };

  const getOrderStatus = (order) => {
    if (!order) {
      return { status: 'Unknown', variant: 'secondary' };
    }
    
    const status = order.status || 'Unknown';
    let variant = 'secondary';
    
    if (status.toLowerCase().includes('delivered')) variant = 'success';
    else if (status.toLowerCase().includes('job done') || status.toLowerCase().includes('job_done')) variant = 'warning';
    else if (status.toLowerCase().includes('wip') || status.toLowerCase().includes('progress')) variant = 'info';
    else if (status.toLowerCase().includes('pending')) variant = 'info';
    if (status.toLowerCase().includes('approved') && !status.toLowerCase().includes('customer')) variant = 'warning';
    if (status.toLowerCase().includes('supervisor_approved') || status.toLowerCase().includes('supervisor approved')) variant = 'warning';
    if (status.toLowerCase().includes('customer_approved') || status.toLowerCase().includes('customer approved')) variant = 'success';
    if (status.toLowerCase().includes('customer_rejected') || status.toLowerCase().includes('customer rejected')) variant = 'danger';
    
    return { status, variant };
  };

  const getQuoteDisplayName = (quote) => {
    return quote.customer?.companyName || quote.customer?.company || quote.companyName || quote.company || quote.customer?.email || quote.email || 'Quote';
  };

  const getQuoteDescription = (quote) => {
    return quote.materials || quote.description || quote.jobDescription || 'Quote request';
  };

  const getQuoteStatus = (quote) => {
    if (!quote) {
      return { status: 'Unknown', variant: 'secondary' };
    }
    
    const status = quote.status || 'Unknown';
    let variant = 'secondary';
    
    if (status.toLowerCase().includes('customer_approved') || status.toLowerCase().includes('customer approved')) variant = 'success';
    else if (status.toLowerCase().includes('approved') || status.toLowerCase().includes('accepted')) variant = 'warning';
    else if (status.toLowerCase().includes('pending')) variant = 'info';
    else if (status.toLowerCase().includes('rejected') || status.toLowerCase().includes('declined')) variant = 'danger';
    
    return { status, variant };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      // Use toLocaleDateString with UTC timezone to prevent shifts
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getUTCFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
        timeZone: 'UTC'  // Force UTC to prevent timezone conversion
      });
    } catch {
      return '';
    }
  };

  return {
    // State
    query,
    showResults,
    loading,
    searchResults,
    debouncedQuery,
    
    // Data
    allData,
    
    // Handlers
    handleInputChange,
    handleResultClick,
    handleKeyDown,
    handleFocus,
    handleBlur,
    clearSearch,
    
    // Helper functions
    getCustomerDisplayName,
    getCustomerEmail,
    getOrderDisplayName,
    getOrderCompany,
    getOrderStatus,
    getQuoteDisplayName,
    getQuoteDescription,
    getQuoteStatus,
    formatDate,
    
    // Computed properties
    isSearching: query !== debouncedQuery,
    totalDataCount: customers.length + orders.length + quotes.length
  };
};

export default useGlobalSearch; 